import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { FieldList, MetaBox, MetaBoxLayout, PublishBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import { MemberPhotoField } from '@/features/members/MemberPhotoField';
import {
  MEMBER_STATUS_LABELS,
  deleteOwnPhoto,
  fetchOwnMember,
  updateOwnMember,
  uploadOwnPhoto,
  type Member,
} from '@/features/members/api';
import { ChannelPrefsBox } from '@/features/notifications/ChannelPrefsBox';
import { fetchMyChannelPrefs, updateMyChannelPref } from '@/features/notifications/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

/** 本人が編集できるテキスト項目（先方のマイページ様式 / D-148） */
type FormKey =
  | 'name'
  | 'name_kana'
  | 'company'
  | 'position'
  | 'company_url'
  | 'specialty'
  | 'subordinate_count'
  | 'years_of_service'
  | 'hobbies'
  | 'career_history'
  | 'participation_purpose'
  | 'strengths'
  | 'weaknesses'
  | 'goals'
  | 'bio'
  | 'phone'
  | 'contact_email';

const FORM_FIELDS: {
  key: FormKey;
  label: string;
  kind: 'text' | 'number' | 'textarea';
  hint?: string;
}[] = [
  { key: 'name', label: '氏名', kind: 'text' },
  { key: 'name_kana', label: 'ふりがな', kind: 'text' },
  { key: 'company', label: '企業名', kind: 'text' },
  {
    key: 'position',
    label: '役職',
    kind: 'text',
    hint: '企業名・役職・氏名を変えると、事務局にお知らせが届きます。',
  },
  { key: 'company_url', label: '企業HP', kind: 'text' },
  { key: 'specialty', label: '管掌領域', kind: 'text' },
  { key: 'subordinate_count', label: '部下人数', kind: 'number' },
  { key: 'years_of_service', label: '勤続年数', kind: 'number' },
  { key: 'hobbies', label: '趣味・特技・最近のマイブーム', kind: 'textarea' },
  { key: 'career_history', label: '経歴', kind: 'textarea' },
  { key: 'participation_purpose', label: 'CXO Growth参加目的', kind: 'textarea' },
  { key: 'strengths', label: '強み', kind: 'textarea' },
  { key: 'weaknesses', label: '弱み', kind: 'textarea' },
  { key: 'goals', label: '目標', kind: 'textarea' },
  { key: 'bio', label: '自己紹介', kind: 'textarea' },
  {
    key: 'phone',
    label: '電話番号',
    kind: 'text',
    hint: '電話番号・連絡先メールは他の受講生には公開されません。',
  },
  { key: 'contact_email', label: '連絡先メール', kind: 'text' },
];

function initialForm(member: Member): Record<FormKey, string> {
  const form = {} as Record<FormKey, string>;
  for (const field of FORM_FIELDS) {
    const value = member[field.key];
    form[field.key] = value === null || value === undefined ? '' : String(value);
  }
  return form;
}

/**
 * マイプロフィール（自分のみ全項目 / 指示書 §6）。
 * 項目は先方のマイページ様式に合わせている（D-148）。M2 のメタボックスを使い回す。
 */
export function ProfilePage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['member', 'me'], queryFn: fetchOwnMember });

  const [form, setForm] = useState<Record<FormKey, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.data) setForm(initialForm(query.data));
  }, [query.data]);

  if (query.isPending || form === null) return <SkeletonRows rows={4} />;
  if (query.isError) {
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  async function onSave(): Promise<void> {
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      await updateOwnMember({
        name: form.name,
        name_kana: form.name_kana,
        company: form.company,
        position: form.position,
        company_url: form.company_url,
        specialty: form.specialty,
        subordinate_count:
          form.subordinate_count === '' ? null : Number(form.subordinate_count),
        years_of_service: form.years_of_service === '' ? null : Number(form.years_of_service),
        hobbies: form.hobbies,
        career_history: form.career_history,
        participation_purpose: form.participation_purpose,
        strengths: form.strengths,
        weaknesses: form.weaknesses,
        goals: form.goals,
        bio: form.bio,
        phone: form.phone,
        contact_email: form.contact_email,
      });
      await queryClient.invalidateQueries({ queryKey: ['member', 'me'] });
      setNotice('プロフィールを更新しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const member = query.data;

  return (
    <>
      <PageHeader
        title="マイプロフィール"
        description="ここに書いた内容（電話番号・連絡先メールを除く）は、他の受講生や理事・講師の先生方にも表示されます。"
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {/* 顔写真は必須（2026-08-18 先方回答 / D-104。自動の督促は行わず画面内の案内のみ） */}
      {!member.photo_url && (
        <Notice kind="warning" dismissible={false}>
          <strong>顔写真の登録は必須です。</strong>
          下の「顔写真」からアップロードしてください（月例会・分科会の当日、
          理事・講師の先生方があなたを確認するために使います）。
        </Notice>
      )}

      <MetaBoxLayout
        main={
          <MetaBox title="プロフィール編集">
            <div className="mb-4">
              <span className="mb-1.5 block text-wptext-muted">
                顔写真 <span className="text-wpnotice-error">（必須）</span>
              </span>
              <MemberPhotoField
                photoUrl={member.photo_url}
                name={member.name}
                editable
                onUpload={async (file) => {
                  await uploadOwnPhoto(file);
                  await queryClient.invalidateQueries({ queryKey: ['member', 'me'] });
                  setNotice('顔写真を更新しました。');
                }}
                onDelete={async () => {
                  await deleteOwnPhoto();
                  await queryClient.invalidateQueries({ queryKey: ['member', 'me'] });
                  setNotice('顔写真を削除しました。');
                }}
              />
              <p className="mt-1 text-xs text-wptext-muted">
                月例会や分科会の当日、理事・講師の先生方があなたを確認するために使います。
              </p>
            </div>

            {FORM_FIELDS.map((field) => (
              <div key={field.key}>
                <label htmlFor={`profile-${field.key}`} className="mb-1.5 block text-wptext-muted">
                  {field.label}
                </label>
                {field.kind === 'textarea' ? (
                  <textarea
                    id={`profile-${field.key}`}
                    rows={3}
                    value={form[field.key]}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    className={`${field.hint ? 'mb-1' : 'mb-4'} w-full rounded-sm border border-wpborder px-3 py-2`}
                  />
                ) : (
                  <input
                    id={`profile-${field.key}`}
                    type={field.kind === 'number' ? 'number' : 'text'}
                    min={field.kind === 'number' ? 0 : undefined}
                    value={form[field.key]}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    className={`${field.hint ? 'mb-1' : 'mb-4'} w-full rounded-sm border border-wpborder px-3 py-2`}
                  />
                )}
                {field.hint && <p className="mb-4 text-xs text-wptext-muted">{field.hint}</p>}
              </div>
            ))}
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSave()}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              保存
            </button>
          </MetaBox>
        }
        side={
          <PublishBox
            statusLabel={MEMBER_STATUS_LABELS[member.status]}
            updatedAt={member.updated_at ? formatDateTime(member.updated_at) : null}
            primaryLabel="保存"
            onPrimary={() => void onSave()}
            primaryDisabled={busy}
          />
        }
        below={
          <>
            <ChannelPrefsBox
              queryKey={['channel-prefs', 'me']}
              fetchPrefs={fetchMyChannelPrefs}
              updatePref={updateMyChannelPref}
            />
            <MetaBox title="事務局が管理する情報">
              <FieldList
                fields={[
                  { label: '会員No.', value: member.member_id },
                  { label: '期', value: member.cohort_name ?? '—' },
                  { label: 'コース', value: member.course_name ?? '—' },
                ]}
              />
            </MetaBox>
          </>
        }
      />
    </>
  );
}
